import {dataTestId} from '@hconnect/uikit'
import {Skeleton} from '@mui/material'
import {useIsMutating} from '@tanstack/react-query'
import * as d3 from 'd3'
import {throttle} from 'lodash'
import {useRef, useLayoutEffect, useState, useEffect, memo, cloneElement} from 'react'

import {GANTT_CHART_ITEM_HEIGHT, GANTT_CHART_ROW_HEIGHT} from '../constants'
import {toPixel} from '../helpers/utils'
import {GanttChartItemPosition} from '../types/common'

import {TGanttChartItem} from './GanttChartItem'
import {GanttChartItemTooltip} from './GanttChartItemTooltip'

const GANT_ITEM_LABEL_BREAKPOINT_S = 30

export interface GantChartStaticItemProps {
  item: TGanttChartItem
  rowHeight?: number
  rowIndex?: number
  rowPadding?: number
  stepWidth: number
  isReadOnly?: boolean
  mutationKey: string
  onItemClick?: (value: string) => void
}

function _GanttChartStaticItem({
  item,
  rowHeight = GANTT_CHART_ROW_HEIGHT,
  rowIndex = 0,
  rowPadding = 0,
  stepWidth,
  isReadOnly = false,
  mutationKey,
  onItemClick
}: GantChartStaticItemProps) {
  // Check if current item is mutating by mutationKey
  const mutating = useIsMutating({mutationKey: [mutationKey]})
  const isItemLoading = !!mutating
  const shouldDisableItem = isReadOnly || isItemLoading

  const [ganttChartItem, setGanttChartItem] = useState<TGanttChartItem>(item)

  const itemHeight = item.height ?? GANTT_CHART_ITEM_HEIGHT

  const graphItemRef = useRef<HTMLHeadingElement>(null)
  const labelRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    setGanttChartItem(item)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepWidth])

  useLayoutEffect(() => {
    const ganttItemBar = graphItemRef.current
    const ganttItemLabel = labelRef.current

    if (!ganttItemBar || !ganttItemLabel) {
      return
    }

    const ganttItemD3 = d3.select<Element, unknown>(ganttItemBar)
    const labelD3 = d3.select(ganttItemLabel)
    // elements inside label
    const notificationD3 = labelD3.select('#comment_notification')

    const throttledHideCommentNotification = throttle(() => notificationD3.style('display', 'none'))
    const throttledShowCommentNotification = throttle(() =>
      notificationD3.style('display', 'inherit')
    )

    const drawGanttItem = (position: GanttChartItemPosition): void => {
      ganttItemD3.style('width', toPixel(position.itemWidth)).style('left', toPixel(position.start))
      labelD3
        .style('width', toPixel(position.labelWidth))
        .style('left', toPixel(position.labelOffset))

      if (position.labelWidth < GANT_ITEM_LABEL_BREAKPOINT_S) {
        if (item.hasComment) {
          throttledHideCommentNotification()
        }
      } else {
        if (item.hasComment) {
          throttledShowCommentNotification()
        }
      }
    }

    const position = {
      start: ganttChartItem.start,
      end: ganttChartItem.end,
      itemWidth: ganttChartItem.end - ganttChartItem.start,
      labelWidth: ganttChartItem.end - ganttChartItem.start,
      labelOffset: 0
    }

    drawGanttItem(position)
  }, [item, ganttChartItem.end, ganttChartItem.start])

  const handleGanttItemClick = () => {
    if (!shouldDisableItem && onItemClick) {
      onItemClick(ganttChartItem.id)
    }
  }

  const ItemContent = (
    <>
      <div ref={labelRef}>{item.label}</div>
      {isItemLoading && (
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, height: 1}}
        />
      )}
    </>
  )

  return (
    <>
      <div
        ref={graphItemRef}
        style={{
          top: rowHeight * rowIndex + rowPadding,
          height: itemHeight,
          position: 'absolute'
        }}
        onClick={handleGanttItemClick}
        {...dataTestId(`gantt-item-${item.testPrefix ?? ''}${ganttChartItem.id}`)}
      >
        {cloneElement(item.background ? item.background : <></>, {}, ItemContent)}
      </div>
      {item.tooltip && <GanttChartItemTooltip anchorElRef={graphItemRef} {...item.tooltip} />}
    </>
  )
}
export const GanttChartStaticItem = memo(_GanttChartStaticItem)
